import { useState, useEffect } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { broadcastApi, Broadcast } from '../services/api-client';

const typeStyles: Record<string, { bg: string; border: string; icon: string; iconColor: string }> =
  {
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      iconColor: 'text-blue-500',
    },
    warning: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
      iconColor: 'text-amber-500',
    },
    promo: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
      iconColor: 'text-green-500',
    },
    fun: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
      iconColor: 'text-purple-500',
    },
  };

function linkifyContent(text: string) {
  const parts = text.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="underline font-medium hover:opacity-80"
      >
        {part}
      </a>
    ) : (
      part
    )
  );
}

export function BroadcastBanner() {
  const { isAuthenticated } = useAuthContext();
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let mounted = true;
    broadcastApi
      .getActive()
      .then((data) => {
        if (mounted) setBroadcasts(data);
      })
      .catch((err) => {
        console.error('Failed to fetch broadcasts:', err);
      });

    return () => {
      mounted = false;
    };
  }, [isAuthenticated]);

  const handleDismiss = async (id: string) => {
    setBroadcasts((prev) => prev.filter((b) => b.id !== id));
    try {
      await broadcastApi.dismiss(id);
    } catch (err) {
      console.error('Failed to dismiss broadcast:', err);
    }
  };

  if (broadcasts.length === 0) return null;

  return (
    <div className="space-y-0">
      {broadcasts.map((broadcast) => {
        const style = typeStyles[broadcast.broadcastType] || typeStyles.info;
        return (
          <div
            key={broadcast.id}
            className={`${style.bg} border-b ${style.border} px-4 py-2.5 flex items-start gap-3`}
          >
            <svg
              className={`w-5 h-5 ${style.iconColor} flex-shrink-0 mt-0.5`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={style.icon} />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">{broadcast.title}</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">
                {linkifyContent(broadcast.content)}
              </p>
            </div>
            <button
              onClick={() => handleDismiss(broadcast.id)}
              className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-0.5"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}
